import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import Calendar from "react-calendar";
import SubHeader from "components/common/SubHeader";
import { getClassSchedule, getPracticeSchedule } from "api/class";
import CourseCard from "components/card/CourseCard";
import NoData from "components/common/NoData";
import { getLocationList, getSportsTypes } from "api/class";
import { getMyTickets, getPracticeTickets } from "api/user";
import useUserStore from "@store/useUserStore";
import useRefreshCallback from "hooks/useRefreshCallback";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "components/common/LoadingSpiner";
import { TICKET_TYPES } from "pages/schedule/CreateClass";

const TABS = [
    { type: "class", label: "강습" },
    { type: "practice", label: "연습반" },
    { type: "training_special", label: "특강" },
];

// 구버전 URL(?type=training, ?type=special) 하위호환
const normalizeType = (t) => (t === "training" || t === "special" ? "training_special" : t);

const getSchedulesByDate = (schedules, date) =>
    schedules.filter((lesson) => dayjs(lesson.date).isSame(date, "day") && lesson.status !== "CANCELLED");

// 프리미엄 트레이닝(유료 연습반) 판별 — 캘린더에서 일반 연습반과 시각적으로 구분
const isPremiumTraining = (lesson) =>
    lesson?.practice_type === "REGULAR_TRAINING" ||
    !!lesson?.title?.includes("프리미엄 트레이닝");

const RESERVATION_UI_STATE_KEY = "reservation_ui_state";//화면 이탈 후 돌아왔을 때 상태 유지

const Reservation = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const type = normalizeType(searchParams.get("type") || "class");
    const userInfo = useUserStore((state) => state.userInfo);

    const [regularTickets, setRegularTickets] = useState([]);
    const [practiceTickets, setPracticeTickets] = useState([]);
    const tickets = useMemo(() => {
        const regular = regularTickets
            .filter(
                (t) =>
                    t.sessions_used < t.sessions_total &&
                    t.status !== "EXPIRED" &&
                    t.status !== "CANCELLED"
            )
            .map((t) => ({ ...t, _kind: "regular" }));
        const practice = (practiceTickets || [])
            .filter((t) => t.is_valid)
            .map((t) => ({ ...t, _kind: "practice" }));
        return [...regular, ...practice];
    }, [regularTickets, practiceTickets]);
    const today = dayjs();

    // sessionStorage에서 이전 UI 상태 복원 (mount 시 1회만 읽음)
    const savedUIState = useRef((() => {
        try {
            const raw = sessionStorage.getItem(RESERVATION_UI_STATE_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    })());

    const [isLoading, setIsLoading] = useState(false);
    const [extendedTicket, setExtendedTicket] = useState(false);
    // 저장된 selectedDate가 과거(오늘 이전)면 stale로 간주하고 today로 폴백
    const savedSelected = savedUIState.current?.selectedDate;
    const savedIsFuture =
        !!savedSelected && !dayjs(savedSelected).isBefore(today, "day");
    const [searchDate, setSearchDate] = useState(
        savedIsFuture && savedUIState.current?.searchDate
            ? savedUIState.current.searchDate
            : {
                  startDate: today.startOf("month").format("YYYY-MM-DD"),
                  endDate: today.endOf("month").format("YYYY-MM-DD"),
              }
    );
    const [selectedDate, setSelectedDate] = useState(
        savedIsFuture ? dayjs(savedSelected).toDate() : today.toDate()
    );
    const [schedules, setSchedules] = useState([]);
    const [selectedDaySchedules, setSelectedDaySchedules] = useState([]);
    const [filterOptions, setFilterOptions] = useState({
        sport: [],
        location: [{ name: "장소 전체", id: null }],
        // ticket: [{ name: "이용권 종류", id: null }],
    });
    const [selectedFilter, setSelectedFilter] = useState({
        sport: null,
        location: { name: "장소 전체", id: null },
        // ticket: { name: "이용권 종류", id: null },
    });
    const [isFilterOptionsLoaded, setIsFilterOptionsLoaded] = useState(false);
    const reservationListRef = useRef(null);
    // 저장된 날짜가 미래면 자동 날짜 선택 건너뜀 (과거면 today로 폴백했으므로 자동 선택 활성)
    const shouldAutoSelectDate = useRef(!savedIsFuture);
    // { [cacheKey]: rawData } — 탭/월 기준으로 캐시
    const scheduleCache = useRef({});

    // 이용권 필터 적용 (allowed_ticket_types 기준, 클라이언트 필터링)
    // 정규수강권은 allowed_ticket_types === "NONE"
    const ticketFilteredSchedules = useMemo(() => {
        if (!selectedFilter.ticket?.id) return schedules;
        // 정규수강권(REGULAR)은 API에서 allowed_ticket_types === "NONE"으로 저장됨
        const allowedType =
            selectedFilter.ticket.id === "REGULAR"
                ? "NONE"
                : selectedFilter.ticket.id;
        return schedules.filter(
            (lesson) => lesson.allowed_ticket_types === allowedType
        );
    }, [schedules, selectedFilter.ticket]);

    // ticketFilteredSchedules 또는 selectedDate 변경 시 selectedDaySchedules 업데이트
    useEffect(() => {
        setSelectedDaySchedules(
            getSchedulesByDate(ticketFilteredSchedules, selectedDate)
        );
    }, [ticketFilteredSchedules, selectedDate]);

    const applySchedules = useCallback((res) => {
        setSchedules(res);
        if (shouldAutoSelectDate.current) {
            shouldAutoSelectDate.current = false;
            const earliest = res
                .filter((s) => s.status !== 'CANCELLED')
                .map((s) => dayjs(s.date))
                .filter((d) => !d.isBefore(dayjs(), 'day'))
                .sort((a, b) => a.valueOf() - b.valueOf())[0];

            if (earliest) setSelectedDate(earliest.toDate());
        }
    }, []);

    const fetchData = useCallback(async () => {
        const locKey = selectedFilter.location?.id ?? "all";
        const sportKey = selectedFilter.sport?.id ?? "all";
        const classCacheKey = `class_${searchDate.startDate}_sport${sportKey}_loc${locKey}`;
        const practiceCacheKey = `practice_${searchDate.startDate}_sport${sportKey}_loc${locKey}`;

        const needsClass = type === "class" || type === "training_special";
        const needsPractice = type === "practice" || type === "training_special";

        const sortByDateTime = (a, b) => {
            const at = dayjs(`${a.date} ${a.start_time || "00:00"}`).valueOf();
            const bt = dayjs(`${b.date} ${b.start_time || "00:00"}`).valueOf();
            return at - bt;
        };

        const applyByType = (classRaw, practiceRaw) => {
            if (type === "class") {
                // 강습: 특강(GENERAL_1T10) 제외
                return classRaw.filter(
                    (l) => l.allowed_ticket_types !== "GENERAL_1T10"
                );
            }
            if (type === "practice") {
                return practiceRaw;
            }
            if (type === "training_special") {
                // 특강(강습 API의 GENERAL_1T10)만 표시 (트레이닝은 연습반 탭으로 이동)
                const special = classRaw.filter(
                    (l) => l.allowed_ticket_types === "GENERAL_1T10"
                );

                return [...special].sort(sortByDateTime);
            }
            return [];
        };

        try {
            const params = {
                startDate: searchDate.startDate,
                endDate: searchDate.endDate,
                ...(selectedFilter.sport?.id && {
                    sport: selectedFilter.sport.id,
                }),
                ...(!selectedFilter.location?.name.includes("전체") &&
                    selectedFilter.location?.name && {
                        location: selectedFilter.location.name,
                    }),
            };

            const fetchClass = async () => {
                if (!needsClass) return null;
                if (scheduleCache.current[classCacheKey] !== undefined) {
                    return scheduleCache.current[classCacheKey];
                }
                const data = await getClassSchedule(params);
                scheduleCache.current[classCacheKey] = data;
                return data;
            };
            const fetchPractice = async () => {
                if (!needsPractice) return null;
                if (scheduleCache.current[practiceCacheKey] !== undefined) {
                    return scheduleCache.current[practiceCacheKey];
                }
                const data = await getPracticeSchedule(params);
                scheduleCache.current[practiceCacheKey] = data;
                return data;
            };

            const [classRaw, practiceRaw] = await Promise.all([
                fetchClass(),
                fetchPractice(),
            ]);

            applySchedules(applyByType(classRaw, practiceRaw));
            setIsLoading(false);
        } catch (error) {
            console.error("데이터 로딩 중 오류 발생:", error);
            setIsLoading(false);
        }
    }, [type, searchDate, selectedFilter, applySchedules]);

    const fetchFilterOptions = useCallback(async () => {
        setIsLoading(true);
        try {
            const [locationListRes, sportListRes] = await Promise.all([
                getLocationList(),
                getSportsTypes(),
            ]);
            const ticketOptions = [
                // { id: null, name: "이용권 종류" },
                // { id: "REGULAR", name: "정규수강권" }, 
                ...Object.entries(TICKET_TYPES).map(([key, name]) => ({
                    id: key,
                    name,
                })),
            ];
            const sportOptions = sportListRes.sports.map((sport) => ({
                id: sport.id,
                name: sport.name,
            }));
            setFilterOptions({
                sport: sportOptions,
                location: [
                    { id: null, name: "장소 전체" },
                    ...locationListRes.locations.map((loc) => ({
                        id: loc.id,
                        name: loc.name,
                    })),
                ],
                ticket: ticketOptions,
            });
            const { preferred_sport } = useUserStore.getState().userInfo ?? {};

            // 저장된 스포츠 필터를 선호 스포츠보다 우선 복원
            // (location/ticket과 동일한 세션 복원 규칙)
            const savedSport = savedUIState.current?.selectedFilterSportId != null
                ? sportOptions.find((s) => s.id === savedUIState.current.selectedFilterSportId)
                : null;
            const defaultSport =
                savedSport ||
                sportOptions.find((s) => s.id === preferred_sport) ||
                null;

            const savedLocation = savedUIState.current?.selectedFilterLocationId != null
                ? locationListRes.locations.find((l) => l.id === savedUIState.current.selectedFilterLocationId)
                : null;
            const defaultLocation = savedLocation
                ? { id: savedLocation.id, name: savedLocation.name }
                : { id: null, name: "장소 전체" };

            const savedTicket = savedUIState.current?.selectedFilterTicketId != null
                ? ticketOptions.find((t) => t.id === savedUIState.current.selectedFilterTicketId)
                : null;
            const defaultTicket = savedTicket; //|| { id: null, name: "이용권 종류" };

            setSelectedFilter((prev) => ({
                ...prev,
                sport: defaultSport,
                location: defaultLocation,
                ticket: defaultTicket,
            }));
            setIsFilterOptionsLoaded(true);
            // isLoading은 fetchData 완료 후 해제 (초기 날짜 플래시 방지)
            return true;
        } catch (error) {
            console.error("데이터 로딩 중 오류 발생:", error);
            return false;
        }
    }, []);

    // 초기 로딩
    useEffect(() => {
        fetchFilterOptions();
    }, [fetchFilterOptions]);

    // 수강권(정규/연습반) 초기 조회
    useEffect(() => {
        if (userInfo) {
            getMyTickets().then((res) => setRegularTickets(res || []));
            getPracticeTickets().then((res) => setPracticeTickets(res || []));
        }
    }, [userInfo]);

    // Pull-to-Refresh — 캐시 초기화 후 재로딩
    useRefreshCallback(() => {
        scheduleCache.current = {};
        fetchFilterOptions();
    }, [fetchFilterOptions]);

    // 날짜 자동 선택은 최초 mount(초기 진입) 시에만 적용한다.
    // 탭(type)·필터(스포츠/장소/이용권) 변경 시에는 리셋하지 않아 사용자가 선택한 날짜를 유지한다.
    // 세션에 복원할 미래 날짜가 있으면(상세 갔다 뒤로 온 경우) 자동 선택이
    // 복원된 날짜를 "가장 빠른 일정 날짜"로 덮어쓰지 않도록 건너뛴다.
    useEffect(() => {
        shouldAutoSelectDate.current = !savedIsFuture;
    }, [savedIsFuture]);

    // 필터 옵션 로딩 완료 후 또는 selectedFilter/type 변경 시 fetchData 실행
    useEffect(() => {
        if (isFilterOptionsLoaded) {
            fetchData();
        }
    }, [isFilterOptionsLoaded, selectedFilter, fetchData]);

    const scrollToReservationList = useCallback(() => {
        if (!reservationListRef.current) return;
        reservationListRef.current.scrollIntoView({
            behavior: "smooth",
        });
    }, []);

    const onClickDay = (date) => {
        scrollToReservationList();
        if (getSchedulesByDate(ticketFilteredSchedules, date).length === 0)
            return;
        setSelectedDate(date);
    };

    const onTabChange = (tabType) => {
        setSearchParams({ type: tabType }, { replace: true });
        setSelectedFilter((prev) => ({
            ...prev,
            // ticket: { name: "이용권 종류", id: null },
        }));
    };

    // 필터/날짜 상태 변경 시 sessionStorage에 자동 저장
    useEffect(() => {
        sessionStorage.setItem(
            RESERVATION_UI_STATE_KEY,
            JSON.stringify({
                selectedDate: dayjs(selectedDate).format("YYYY-MM-DD"),
                searchDate,
                selectedFilterSportId: selectedFilter.sport?.id ?? null,
                selectedFilterLocationId: selectedFilter.location?.id ?? null,
                selectedFilterTicketId: selectedFilter.ticket?.id ?? null,
            })
        );
    }, [selectedDate, searchDate, selectedFilter]);

    if (isLoading) {
        return <LoadingSpinner />;
    }

    const WEEKDAYS_KR = ["일", "월", "화", "수", "목", "금", "토"];
    const selectedDateLabel = `${dayjs(selectedDate).format(
        "M월 D일"
    )} (${WEEKDAYS_KR[dayjs(selectedDate).day()]}) · ${
        selectedDaySchedules.length
    }건`;

    return (
        <div className="page-reservation">
            <SubHeader title="예약하기" />
            <button
                type="button"
                className="curriculum-banner"
                onClick={() => window.open("/promotion/class", "_blank")}
            >
                <span className="curriculum-banner__text">
                    처음이라면? <strong>BUCCL 강습 커리큘럼</strong> 한눈에 보기
                </span>
                <span className="curriculum-banner__arrow" aria-hidden="true">
                    ›
                </span>
            </button>
            {userInfo && (
                <button
                    type="button"
                    className="ticket-banner"
                    aria-expanded={extendedTicket}
                    aria-label={`사용 가능한 수강권 ${
                        extendedTicket ? "접기" : "펼치기"
                    }`}
                    onClick={() => setExtendedTicket(!extendedTicket)}
                >
                    <span className="ticket-banner__icon" aria-hidden="true">🎫</span>
                    <div className="ticket-banner__body">
                        <div className="ticket-banner__title">
                            사용 가능 수강권{" "}
                            <span className="count">{tickets.length}장</span>
                        </div>
                    </div>
                    <span
                        className={`ticket-banner__chevron ${
                            extendedTicket ? "expanded" : ""
                        }`}
                        aria-hidden="true"
                    >
                        ▼
                    </span>
                </button>
            )}
            {userInfo && (
                <div
                    className={`ticket-accordion ${
                        extendedTicket ? "expanded" : "collapsed"
                    }`}
                >
                    {tickets && tickets.length > 0 ? (
                        <ul className="ticket-list">
                            {tickets.map((ticket) => (
                                <li
                                    key={`${ticket._kind}-${ticket.id}`}
                                    className="ticket-item"
                                >
                                    <span className="ticket-name">
                                        {ticket._kind === "practice"
                                            ? "프리미엄 트레이닝"
                                            : ticket.lesson_product_name}
                                    </span>
                                    <span className="ticket-usage">
                                        ({ticket.sessions_used}/
                                        {ticket.sessions_total})
                                    </span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="no-ticket-content">
                            <button
                                className="btn btn-outline-purple500 btn-fit btn-xs"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate("/store/class");
                                }}
                            >
                                수강권 구매하기
                            </button>
                        </div>
                    )}
                </div>
            )}

            <div className="calendar-wrap">
                <Calendar
                    className="buccl-calendar"
                    locale="ko-KR"
                    calendarType="gregory"
                    prevLabel="〈"
                    nextLabel="〉"
                    formatDay={(locale, date) => String(date.getDate())}
                    showNeighboringMonth={false}
                    value={selectedDate}
                    onClickDay={onClickDay}
                    onChange={(date) => {
                        setSelectedDate(date);
                        scrollToReservationList();
                    }}
                    onActiveStartDateChange={(date) => {
                        setSearchDate({
                            startDate: dayjs(date.activeStartDate)
                                .startOf("month")
                                .format("YYYY-MM-DD"),
                            endDate: dayjs(date.activeStartDate)
                                .endOf("month")
                                .format("YYYY-MM-DD"),
                        });
                    }}
                    tileClassName={({ date, view }) => {
                        if (view !== "month") return "";
                        const daySchedules = getSchedulesByDate(
                            ticketFilteredSchedules,
                            dayjs(date).startOf("day")
                        );
                        const classes = ["tile"];
                        const day = date.getDay();
                        if (day === 0) classes.push("sun");
                        if (day === 6) classes.push("sat");
                        if (daySchedules.length === 0) {
                        } else {
                            classes.push("has-schedule-tile");
                        }
                        return classes.join(" ");
                    }}
                    tileContent={({ date, view }) => {
                        if (view !== "month") return null;
                        const day = dayjs(date).startOf("day");
                        // 오늘/선택된 날짜는 링·채움으로 강조되므로 점 생략 (기존 디자인 유지)
                        if (
                            day.isSame(today, "day") ||
                            day.isSame(dayjs(selectedDate), "day")
                        )
                            return null;
                        const daySchedules = getSchedulesByDate(
                            ticketFilteredSchedules,
                            day
                        );
                        if (daySchedules.length === 0) return null;
                        // 연습반(일반) = 보라색, 프리미엄 트레이닝 = 노란색 (보라색 먼저)
                        const hasPractice = daySchedules.some(
                            (l) => !isPremiumTraining(l)
                        );
                        const hasPremium = daySchedules.some((l) =>
                            isPremiumTraining(l)
                        );
                        return (
                            <span className="tile-dots" aria-hidden="true">
                                {hasPractice && (
                                    <span className="tile-dot tile-dot--practice" />
                                )}
                                {hasPremium && (
                                    <span className="tile-dot tile-dot--premium" />
                                )}
                            </span>
                        );
                    }}
                />
            </div>

            <div className="reservation-list-wrap" ref={reservationListRef}>
                <div className="reservation-filter-wrap select-input-wrap">
                    {filterOptions.sport.length > 0 && (
                        <select
                            className={`select chip active`}
                            onChange={(e) => {
                                const selectedItem = filterOptions.sport.find(
                                    (item) => item.id === Number(e.target.value)
                                );
                                if (!selectedItem) return;
                                setSelectedFilter((prev) => ({
                                    ...prev,
                                    sport: selectedItem,
                                }));
                            }}
                            value={selectedFilter.sport?.id || ""}
                        >
                            {filterOptions.sport.map((item) => (
                                <option key={item.id} value={item.id}>
                                    {item.name}
                                </option>
                            ))}
                        </select>
                    )}
                    <select
                        className={`select chip ${
                            !selectedFilter.location?.name.includes("전체")
                                ? "active"
                                : ""
                        }`}
                        onChange={(e) => {
                            const val = e.target.value;
                            const selectedItem =
                                val === ""
                                    ? { id: null, name: "장소 전체" }
                                    : filterOptions.location.find(
                                          (item) => item.id === Number(val)
                                      );
                            if (!selectedItem) return;
                            setSelectedFilter((prev) => ({
                                ...prev,
                                location: selectedItem,
                            }));
                        }}
                        value={selectedFilter.location?.id ?? ""}
                    >
                        {filterOptions.location.map((item) => (
                            <option key={item.id} value={item.id ?? ""}>
                                {item.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="reservation-tab-wrap">
                    {TABS.map((tab) => (
                        <button
                            key={tab.type}
                            className={`tab-item ${
                                type === tab.type ? "active" : ""
                            }`}
                            onClick={() => onTabChange(tab.type)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
                <div className="selected-date-label">
                    <span className="date-text">{selectedDateLabel}</span>
                    {type === "practice" && (
                        <span className="calendar-legend">
                            <span className="legend-item">
                                <span className="legend-dot legend-dot--practice" />
                                연습반
                            </span>
                            <span className="legend-item">
                                <span className="legend-dot legend-dot--premium" />
                                프리미엄 트레이닝
                            </span>
                        </span>
                    )}
                </div>
                {selectedDaySchedules.length > 0 ? (
                  <>
                    {selectedDaySchedules.map((sch) => (
                        <CourseCard
                            key={sch.id}
                            type={
                                sch.approval_status === undefined
                                    ? "practice"
                                    : "class"
                            }
                            schedule={sch}
                        />
                    ))}
                    <div className="lesson-request-banner" data-testid="lesson-request-banner" onClick={() => navigate('/lesson-requests/new')}>
                      <span>※ 강사에게 원하는 날짜와 시간을 요청해보세요.</span>
                      <span>{ `요청하기 >`}</span>
                    </div>
                  </>

                ) : (
                    <NoData
                        title="😱 원하는 일정이 없나요?"
                        desc="강사에게 원하는 날짜와 시간을 요청해보세요."
                        btn={{
                            name: "강습 개설 요청하러 가기",
                            link: "/lesson-requests/new",
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default Reservation;
